// @workflow assistant.voice
/**
 * Every word Read aloud shows or says, in one place (voice showcase, phase 2). The voice is always
 * named from AI_DISCLOSURE.VOICE (src/constants/aiDisclosure.js), the app's one source for AI
 * notices; the failure toasts follow Copy's ("Couldn't copy").
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { AI_DISCLOSURE } from '../../../constants/aiDisclosure.js';
import { READ_ALOUD_CODE as CODE } from './readAloudCodes.js';
import { READ_PHASE, isReading } from './readAloudState.js';
import { clock } from './dictationAudio.js';

const VOICE = AI_DISCLOSURE.VOICE;
const PERCENT = 100;

export const READ_ALOUD_TEXT = Object.freeze({
  READ: `Read aloud (${VOICE})`,
  LOADING: `Loading the ${VOICE}`,
  LOADING_LABEL: `Loading the ${VOICE}. Select to cancel`,
  STOP: `Stop reading (${VOICE})`,
  CAPTION_LOADING: 'Loading…',
  TRY_AGAIN: 'Try again',
});

/** What the reply's live region says. */
export const READ_ALOUD_SAY = Object.freeze({
  READING: `Reading aloud with an ${VOICE}.`,
  FINISHED: 'Finished reading.',
  STOPPED: 'Stopped reading.',
});

const FAILURES = Object.freeze({
  [CODE.FAILED]: Object.freeze({ text: "Couldn't read this reply aloud", retry: true }),
  [CODE.UNAVAILABLE]: Object.freeze({ text: "Read aloud isn't available right now", retry: false }),
  [CODE.NOT_FOUND]: Object.freeze({ text: "Couldn't find this reply to read aloud", retry: false }),
  [CODE.NOTHING_TO_READ]: Object.freeze({ text: 'There is nothing in this reply to read aloud', retry: false }),
});

/** The toast for a code; only a failure offers Try again. An unknown code reads as a failure. */
export const readAloudFailure = (code) => FAILURES[code] || FAILURES[CODE.FAILED];

const BUTTONS = Object.freeze({
  [READ_PHASE.IDLE]: Object.freeze({ phase: READ_PHASE.IDLE, icon: 'speaker-wave', label: READ_ALOUD_TEXT.READ, title: READ_ALOUD_TEXT.READ, busy: false }),
  [READ_PHASE.LOADING]: Object.freeze({ phase: READ_PHASE.LOADING, icon: null, label: READ_ALOUD_TEXT.LOADING_LABEL, title: READ_ALOUD_TEXT.LOADING, busy: true }),
  [READ_PHASE.PLAYING]: Object.freeze({ phase: READ_PHASE.PLAYING, icon: 'stop', label: READ_ALOUD_TEXT.STOP, title: READ_ALOUD_TEXT.STOP, busy: false }),
});

/** One reply's button: a speaker, a spinner while loading, Stop while playing. */
export function readAloudButton(now, messageId) {
  return BUTTONS[isReading(now, messageId) ? now.phase : READ_PHASE.IDLE];
}

/**
 * The quiet line under a reply's actions while it loads or plays, or null: the voice and
 * "Loading…", then the time, with the total and the bar once the whole audio is in.
 */
export function readAloudCaption(state, messageId) {
  const { now, progress } = state;
  if (!isReading(now, messageId)) return null;
  if (now.phase === READ_PHASE.LOADING) return { voice: VOICE, loading: true, text: READ_ALOUD_TEXT.CAPTION_LOADING, percent: null };
  const { played, duration } = progress;
  if (!duration) return { voice: VOICE, loading: false, text: clock(played), percent: null };
  return { voice: VOICE, loading: false, text: `${clock(played)} / ${clock(duration)}`, percent: Math.min(PERCENT, Math.round((played / duration) * PERCENT)) };
}
